import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import useAppointmentSelectionStore from './appointment-selection.store';
import useClientInformationStore from './client-information.store';
import usePatientInformationStore from './patient-information.store';
import useReasonForVisitStore, { ReasonForVisitType } from './reason-for-visit.store';
import useTimeSlotStore from './time-slot.store';

import { mountDevTools } from '@src/store/utils/mount-dev-tools';

type ApplicationRouteConfig = {
	route: string;
	title: string;
	isComplete: () => boolean;
};

export const APPLICATION_ROUTES: Record<string, ApplicationRouteConfig> = {
	clientRegistration: {
		route: `/client-registration`,
		title: 'Client Information',
		isComplete: () => {
			const { clientId, firstName, lastName, email } = useClientInformationStore.getState();
			return Boolean(clientId) || (Boolean(firstName) && Boolean(lastName) && Boolean(email));
		},
	},
	patientRegistration: {
		route: `/patient-registration`,
		title: 'Pet Information',
		isComplete: () => {
			const { patientId, petName, species } = usePatientInformationStore.getState();
			return Boolean(patientId) || (Boolean(petName) && Boolean(species));
		},
	},
	reasonForVisit: {
		route: `/reason-for-visit`,
		title: 'Reason for Visit',
		isComplete: () => {
			const { mainReasonForVisit, reasonsForVisitList } = useReasonForVisitStore.getState();
			if (!mainReasonForVisit) return false;
			if (mainReasonForVisit === ReasonForVisitType.SickNeeds) return reasonsForVisitList.length > 0;
			return true;
		},
	},
	patientHealthInfo: {
		route: `/patient-health-info`,
		title: 'Pet Health Information',
		isComplete: () => {
			const { notes, notesReasonForVisit } = usePatientInformationStore.getState();
			const { mainReasonForVisit, reasonsForVisitList } = useReasonForVisitStore.getState();
			if (Object.keys(notes).length === 0 || notesReasonForVisit !== mainReasonForVisit) return false;
			if (mainReasonForVisit === ReasonForVisitType.SickNeeds) {
				return reasonsForVisitList.every((reason) => reason.replaceAll(',', '') in notes);
			}
			return true;
		},
	},
	appointmentSelection: {
		route: `/appointment-selection`,
		title: 'Date/Time/Location',
		isComplete: () => {
			const { timeSlot } = useTimeSlotStore.getState();
			return Boolean(timeSlot?.date) && Boolean(timeSlot?.time);
		},
	},
	appointmentReview: {
		route: `/appointment-review`,
		title: 'Appointment Review',
		isComplete: () => {
			const { appointmentId } = useAppointmentSelectionStore.getState();
			return Boolean(appointmentId);
		},
	},
	confirmation: {
		route: `/confirmation`,
		title: 'Confirmation',
		isComplete: () => {
			const { appointmentId } = useAppointmentSelectionStore.getState();
			return Boolean(appointmentId);
		},
	},
};

const toRouteObject = ({ route, title }: ApplicationRouteConfig): RouteObject => ({ route, title });

export function getRouteIsComplete(route: string): boolean {
	const config = Object.values(APPLICATION_ROUTES).find((applicationRoute) => applicationRoute.route === route);
	return config?.isComplete() ?? false;
}

type RouteObject = {
	route: string;
	title: string;
};

type ExpandedRouteObject = RouteObject & {
	index: number;
	displayIndex: number;
};

interface RouteHandlerState {
	routesInitialized: boolean;
	routes: RouteObject[];
	currentRouteIndex: number;
}

type RouteHandlerActions = {
	routeProgress: () => {
		previousRoutes: ExpandedRouteObject[];
		currentRoute: ExpandedRouteObject;
		upcomingRoutes: ExpandedRouteObject[];
	};
	initializeRoutes: (userType: 'new' | 'existing', currentRouteIndex?: number, redirectTo?: string | null) => void;
	getNextRoute: () => RouteObject | undefined;
	advanceToNextRoute: (callback: (route: string) => void) => void;
	advanceToFirstIncompleteRoute: (callback: (route: string) => void) => void;
	navToPriorRoute: (route: string, callback: (route: string) => void) => void;
	handleBackButton: (callback: (route: string) => void) => void;
	reviewStepActive: () => boolean;
	removeRoute: (routeToRemove: string) => void;
	reset: () => void;
	setCurrentRouteIndex: (index: number) => void;
};

function routeMatches(configuredRoute: string, requestedRoute?: string | null): boolean {
	if (!requestedRoute) {
		return false;
	}

	const normalizedRequestedRoute = requestedRoute.startsWith('/') ? requestedRoute : `/${requestedRoute}`;

	return configuredRoute === normalizedRequestedRoute || configuredRoute.endsWith(normalizedRequestedRoute);
}

// appointmentSelection is always deferred to just before appointmentReview
const INTELLIGENT_SCHEDULING_INTAKE_STEPS: RouteObject[] = [
	toRouteObject(APPLICATION_ROUTES.patientRegistration),
	toRouteObject(APPLICATION_ROUTES.patientHealthInfo),
	toRouteObject(APPLICATION_ROUTES.appointmentSelection),
	toRouteObject(APPLICATION_ROUTES.appointmentReview),
	toRouteObject(APPLICATION_ROUTES.confirmation),
];

type SetState = { routes: RouteObject[]; currentRouteIndex: number; routesInitialized: true };

function buildIntelligentSchedulingState(
	userType: 'new' | 'existing',
	currentRouteIndex: number,
	redirectTo: string | null | undefined
): SetState {
	if (userType === 'new') {
		const routes: RouteObject[] = [
			toRouteObject(APPLICATION_ROUTES.clientRegistration),
			toRouteObject(APPLICATION_ROUTES.reasonForVisit),
			...INTELLIGENT_SCHEDULING_INTAKE_STEPS,
		];
		return { routes, currentRouteIndex, routesInitialized: true };
	}

	// existing user + Intelligent Scheduling
	const redirectPathname = redirectTo ? redirectTo.split('#')[0].split('?')[0] : '';
	const shouldStartAtPatientReg = routeMatches(APPLICATION_ROUTES.patientRegistration.route, redirectPathname);
	const { patientId } = usePatientInformationStore.getState();

	if (shouldStartAtPatientReg) {
		return {
			routes: [toRouteObject(APPLICATION_ROUTES.reasonForVisit), ...INTELLIGENT_SCHEDULING_INTAKE_STEPS],
			currentRouteIndex: 1,
			routesInitialized: true,
		};
	}

	if (!patientId) {
		return {
			routes: [toRouteObject(APPLICATION_ROUTES.reasonForVisit), ...INTELLIGENT_SCHEDULING_INTAKE_STEPS],
			currentRouteIndex,
			routesInitialized: true,
		};
	}

	// patientId known → skip patientRegistration
	return {
		routes: [
			toRouteObject(APPLICATION_ROUTES.reasonForVisit),
			toRouteObject(APPLICATION_ROUTES.patientHealthInfo),
			toRouteObject(APPLICATION_ROUTES.appointmentSelection),
			toRouteObject(APPLICATION_ROUTES.appointmentReview),
			toRouteObject(APPLICATION_ROUTES.confirmation),
		],
		currentRouteIndex,
		routesInitialized: true,
	};
}

const useRouteHandlerStore = create<RouteHandlerState & RouteHandlerActions>()(
	persist(
		(set, get) => ({
			routesInitialized: false,
			routes: [],
			currentRouteIndex: 0,
			routeProgress: () => {
				const routesCopy = get().routes.map((route, index) => ({ ...route, index, displayIndex: index + 1 }));

				return {
					previousRoutes: routesCopy.slice(0, get().currentRouteIndex),
					currentRoute: routesCopy[get().currentRouteIndex],
					upcomingRoutes: routesCopy.slice(get().currentRouteIndex + 1),
				};
			},
			initializeRoutes: (userType: 'new' | 'existing', currentRouteIndex = 0, redirectTo?: string | null) => {
				set(buildIntelligentSchedulingState(userType, currentRouteIndex, redirectTo));
			},

			removeRoute: (routeToRemove: string) => {
				const removedIndex = get().routes.findIndex((route) => routeMatches(route.route, routeToRemove));
				if (removedIndex === -1) return;

				const updatedRoutes = get().routes.filter((_, routeIndex) => routeIndex !== removedIndex);

				const currentRouteIndex = get().currentRouteIndex;

				// Math.max to safeguard against negative index if the first route is removed
				const updatedCurrentRouteIndex = Math.max(
					0,
					currentRouteIndex >= removedIndex ? currentRouteIndex - 1 : currentRouteIndex
				);

				set({
					routes: updatedRoutes,
					currentRouteIndex: updatedCurrentRouteIndex,
				});
			},

			getNextRoute: () => {
				const routes = get().routes;
				const nextIndex = get().currentRouteIndex + 1;
				return routes[nextIndex];
			},

			advanceToNextRoute: (callback: (route: string) => void) => {
				const routes = get().routes;
				let nextRouteIndex = get().currentRouteIndex + 1;

				// Never skip the last route even if complete — it is the terminal destination.
				while (nextRouteIndex < routes.length - 1 && getRouteIsComplete(routes[nextRouteIndex].route)) {
					nextRouteIndex++;
				}

				set({ currentRouteIndex: nextRouteIndex });
				const nextRoute = routes[nextRouteIndex];

				if (nextRoute) {
					callback(nextRoute.route);
				} else {
					console.error('TODO: Handle error, there is something wrong if this is hit');
				}
			},

			advanceToFirstIncompleteRoute: (callback: (route: string) => void) => {
				const routes = get().routes;
				let targetIndex = 0;

				// Never skip the last route even if complete — it is the terminal destination.
				while (targetIndex < routes.length - 1 && getRouteIsComplete(routes[targetIndex].route)) {
					targetIndex++;
				}

				set({ currentRouteIndex: targetIndex });
				const targetRoute = routes[targetIndex];

				if (targetRoute) {
					callback(targetRoute.route);
				} else {
					console.error('TODO: Handle error, there is something wrong if this is hit');
				}
			},

			navToPriorRoute: (route: string, callback: (route: string) => void) => {
				const indexOfRequestedRoute = get().routes.findIndex((routeObj) => routeMatches(routeObj.route, route));

				if (indexOfRequestedRoute > -1) {
					const requestedRoute = get().routes[indexOfRequestedRoute];
					set({ currentRouteIndex: indexOfRequestedRoute });
					callback(requestedRoute.route);
				} else {
					console.error('TODO: Handle error, there is something wrong if this is hit');
				}
			},

			handleBackButton: (callback: (route: string) => void) => {
				if (get().currentRouteIndex === 0) {
					console.error('TODO: Handle error, there is something wrong if this is hit');
					return;
				}

				set({ currentRouteIndex: get().currentRouteIndex - 1 });
				callback(get().routes[get().currentRouteIndex].route);
			},

			reviewStepActive: () => {
				return get().routes[get().currentRouteIndex].route === APPLICATION_ROUTES.appointmentReview.route;
			},

			reset: () => {
				set({
					routesInitialized: false,
					routes: [],
					currentRouteIndex: 0,
				});
			},

			setCurrentRouteIndex: (index: number) => {
				set({ currentRouteIndex: index });
			},
		}),
		{
			name: 'book-an-appointment-route-handler',
		}
	)
);

mountDevTools('useRouteHandlerStore', useRouteHandlerStore);

export default useRouteHandlerStore;
